import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Container, Grid, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useDispatch, useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  createClinicForm,
  deleteFromById,
  getClinicForms,
  updateFromById,
} from "../../../redux/reducers/clinicSlice";
import { getClinicVets } from "../../../redux/reducers/vetSlice";
import { AppColors } from "../../../util/AppColors";
import { documentTypes } from "../../../util/dropList";

const tableHeaders = [
  "name",
  "documentType",
  "createdBy",
  "lastUpdated",
  "extraStatus",
];
const initValue = { name: "", content: "", documentType: "Form" };

const delBtn = { color: AppColors.red, width: 20, height: 20 };
const edtBtn = { color: AppColors.appPrimary, width: 20, height: 20 };

const Forms = () => {
  const dispatch = useDispatch();
  const contentToPrint = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const [createVsble, setCreateVsble] = useState(false);
  const [formValues, setFormValues] = useState(initValue);
  const [formErrs, setFormErrs] = useState({});
  const [prevModVsble, setPrevModVsble] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [delModVsble, setDelModVsble] = useState(false);
  const clinicForms = useSelector((state) => state?.clinic?.forms);
  const clinicVets = useSelector((state) => state?.vet?.vets?.vets);
  const user = localStorage.getItem("user");
  const profile = JSON.parse(user);

  useEffect(() => {
    dispatch(getClinicForms());
    dispatch(getClinicVets());
  }, []);

  const handleDelete = (frm) => {
    setSelectedForm(frm);
    setDelModVsble(true);
  };

  const handleEdit = (frm) => {
    setFormValues({
      ...formValues,
      name: frm?.name,
      documentType: frm?.documentType,
      content: frm?.content,
    });
    setSelectedForm(frm);
    setCreateVsble(true);
  };

  const getTableForms = () => {
    return clinicForms?.map((cf) => ({
      ...cf,
      documentType:
        cf?.documentType === "Cert" ? "Certificate" : formValues?.documentType,
      createdBy:
        clinicVets?.find((vt) => vt?.doctorId === cf?.assignTo)?.name ?? "-",
      lastUpdated: moment(new Date(cf?.updatedDate)).format("DD MMM YYYY"),
      extraStatus: (
        <div className="flex-row">
          <div className="cursor" onClick={() => handleDelete(cf)}>
            <div className="flex-center">
              <DeleteOutlineIcon sx={delBtn} />
            </div>
            <Typography className="red3 fs8 txt-semi-bold text-align-center">
              Delete
            </Typography>
          </div>
          <div className="cursor ml20" onClick={() => handleEdit(cf)}>
            <div className="flex-center">
              <EditIcon sx={edtBtn} />
            </div>
            <Typography className="blue-color fs8 txt-semi-bold text-align-center">
              Edit
            </Typography>
          </div>
        </div>
      ),
    }));
  };

  const handleCreate = () => {
    setCreateVsble(true);
  };

  const handleChange = (name, value) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSuccess = () => {
    dispatch(getClinicForms());
    setCreateVsble(false);
    setFormValues(initValue);
    setFormErrs({});
  };

  const onSave = () => {
    if (formValues?.name?.length === 0) {
      return setFormErrs({ name: true });
    }
    const data = {
      name: formValues?.name,
      documentType: formValues?.documentType,
      content: formValues?.content,
      signed: "false",
    };
    if (selectedForm) {
      dispatch(updateFromById({ formId: selectedForm?.formId, data })).then(
        (res) => {
          if (res?.payload) {
            handleSuccess();
          }
        }
      );
    } else {
      dispatch(createClinicForm(data)).then((res) => {
        if (res?.payload) {
          handleSuccess();
        }
      });
    }
  };

  const onCloseDelMod = () => {
    setDelModVsble(false);
    setSelectedForm(null);
  };

  const onCofirmDelete = () => {
    if (!selectedForm) return;
    dispatch(deleteFromById(selectedForm?.formId)).then((res) => {
      if (res?.payload) {
        setDelModVsble(false);
        dispatch(getClinicForms());
        setSelectedForm(null);
      }
    });
  };

  const handlePrint = useReactToPrint({
    documentTitle: `${formValues?.name}-${Date.now()}`,
    removeAfterPrint: true,
  });

  return (
    <>
      <Container maxWidth="xl">
        <div className="com-mar back-white pv10">
          {createVsble ? (
            <div className="scroll-70vh">
              <div className="ph10Per mt20">
                <Grid container spacing={2} className="mb20">
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <CustomTextField
                      fullWidth
                      value={formValues?.name}
                      handleChange={(e) =>
                        handleChange("name", e?.target?.value)
                      }
                      label="Name"
                      error={formErrs?.name}
                      helperText={
                        formErrs?.name ? "This field is required" : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                    <CustomSelect
                      list={documentTypes}
                      value={formValues?.documentType}
                      label="Document Type"
                      handleChange={(e) =>
                        handleChange("documentType", e?.target?.value)
                      }
                      select
                    />
                  </Grid>
                  <div className="flex1-end">
                    <div>
                      <CustomButton
                        text="Preview"
                        smallBtn
                        onClick={() => setPrevModVsble(true)}
                      />
                    </div>
                    <div className="ml20">
                      <CustomButton
                        text={selectedForm ? "Update" : "Save"}
                        smallBtn
                        onClick={onSave}
                      />
                    </div>
                  </div>
                </Grid>

                <ReactQuill
                  onChange={(e) => handleChange("content", e)}
                  value={formValues?.content}
                />
                <div className="mt100">
                  <div className="flex-row-ali-cen">
                    {formValues?.documentType === "Form" ? (
                      <Typography className="fs16 font-bold black">
                        Signature of pet parent
                      </Typography>
                    ) : null}
                    <div className="flex1-end">
                      <Typography className="fs16 font-bold black">
                        Signature of licensed veterinarian
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                xl={12}
                className="flex-row-center"
              >
                <div className="flex-row">
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    style={{ width: "600px" }}
                  >
                    <CustomTextField
                      placeholder="Search"
                      fullWidth
                      backgroundColor={AppColors.lightPink}
                      value={searchValue}
                      handleChange={(e) => setSearchValue(e.target.value)}
                    />
                  </Grid>
                </div>
                <div className="w5Per ml20">
                  <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
                    <div>
                      <CustomButton
                        text="Create"
                        smallBtn
                        onClick={handleCreate}
                      />
                    </div>
                  </Grid>
                </div>
              </Grid>
              <CustomTable
                columns={tableHeaders}
                datas={getTableForms()}
                grey
              />
            </div>
          )}
        </div>
      </Container>
      <CustomModal
        open={delModVsble}
        onClose={onCloseDelMod}
        header="Delete Form"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to delete the form?
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="clinic-mod-btn-pos mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                redBtn
                onClick={onCofirmDelete}
                smallBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={onCloseDelMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>

      <CustomModal
        open={prevModVsble}
        onClose={() => setPrevModVsble(false)}
        header=""
        modal
        modalWidth={90}
      >
        <div className="scroll-70vh">
          <div ref={contentToPrint} className="bg ph20">
            <div className="flex-row-ali-cen mv20">
              <div className="w20Per">
                <div className="pl20">
                  <div>
                    {profile?.image ? (
                      <img src={profile?.image} className="img-h75" alt="" />
                    ) : (
                      <div className="flex-center h50img">
                        <Typography className="font-bold fs30 white-color capitalize">
                          {profile?.name?.[0]}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="w60Per flex-center">
                <div className="font-bold fs30 blue-color">
                  {formValues?.name}
                </div>
              </div>
              <div className="w20Per flex1-end">
                <div className="pr10">
                  <img
                    src={require("../../../assets/images/png/VetInstantLogo.png")}
                    alt=""
                    className="wh50 blu-back p5"
                  />
                </div>
              </div>
            </div>
            <div className="yellow-bar" />
            <div dangerouslySetInnerHTML={{ __html: formValues?.content }} />
            <div className="mt100">
              <div className="flex-row-ali-cen">
                {formValues?.documentType === "Form" ? (
                  <Typography className="fs16 font-bold black">
                    Signature of pet parent
                  </Typography>
                ) : null}
                <div className="flex1-end">
                  <Typography className="fs16 font-bold black">
                    Signature of licensed veterinarian
                  </Typography>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-center">
            <div className="mt20">
              <CustomButton
                text="Download"
                onClick={() => handlePrint(null, () => contentToPrint.current)}
                smallBtn
              />
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default Forms;
